import React, { useState, useMemo, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import PageHeader from './PageHeader';
import SearchFilters from '../Search/SearchFilters';
import DataTable from './DataTable';
import ViewDropdown from './ViewDropdown';
import ViewConfigModal from './ViewConfigModal';
import ExportModal from './ExportModal';
import Toast from '../Popup/Toast';
import { exportToExcel, exportToCSV } from '../../utils/exportUtils';
import './ListViewPage.css';

/**
 * ListViewPage Component
 * 
 * A reusable component that provides a standardized layout for list-based pages
 * with search, filters, views, export, and split active/completed sections.
 * 
 * Used by Tasks, Items, and Approvals pages.
 */
export default function ListViewPage({
  // Page configuration
  title,
  itemLabel = 'items',
  tableName,
  
  // Data and loading
  activeData = [],
  completedData = [],
  loading = false,
  error = null,
  
  // Columns configuration
  columns = [],
  completedColumns = null, // Optional separate columns for completed section
  defaultVisibleColumns = [],
  visibleColumns, // Optional: external visible columns control
  onToggleColumn, // Optional: external column toggle handler
  
  // Search and filtering
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  extraFilters = [], // Secondary filters shown behind the "More filters" button
  searchActions = null,
  
  // Sorting
  sortConfig = { key: '', direction: 'asc' },
  onSort,
  completedSortConfig = null, // Optional separate sort config for completed section
  onCompletedSort = null,
  
  // Actions and handlers
  onRowClick,
  onRowRightClick,
  actionButtons = [],
  
  // Split view configuration
  completedSectionTitle = 'Completed Items',
  completedSectionIcon = 'bi-check-circle',
  showCompletedByDefault = false,
  
  // View management (optional)
  useViews = false,
  userViews = [],
  currentView = 'default',
  onViewChange,
  onSaveView,
  onDeleteView,
  onSetDefaultView,
  
  // Export functionality
  enableExport = false,
  exportData,
  exportFileName = 'export',
  exportButtonPosition = 'beforeActions',
  
  // Additional content
  children,
  
  // Custom filtering and sorting logic
  customFilter,
  customSort,
  
  // Modals and notifications
  toast = { show: false, message: '', type: 'success' },
  onCloseToast,
  
  // Additional modals
  additionalModals,
  
  // Expanded rows functionality (optional)
  expandedRows = null,
  expandedRowsData = null,
  renderExpandedRow = null,

  // Multi-select functionality (optional)
  enableMultiSelect = false,
  selectedRows = new Set(),
  onSelectionChange,
  selectableRowKey = 'id',
  bulkActions = [],
  
  // Custom row classes
  getRowClassName = null,

  // Hierarchical child toggle support
  onChildToggle = null,

  // Virtualization (optional)
  virtualizeActiveTable = false,
  virtualizeCompletedTable = false,

  // Column resizing (optional) - widths are shared by the active and completed
  // tables so the two stay visually aligned.
  enableColumnResize = false,
  columnWidths = null,
  onColumnResize = null
}) {
  // Internal state
  const isMobile = useIsMobile();
  const [showCompleted, setShowCompleted] = useState(showCompletedByDefault);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showViewConfigModal, setShowViewConfigModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [internalVisibleColumns, setInternalVisibleColumns] = useState(defaultVisibleColumns);

  // Use external visibleColumns if provided, otherwise use internal state
  const activeVisibleColumns = visibleColumns || internalVisibleColumns;
  
  // For completed section, filter visible columns to match available completed columns
  const completedVisibleColumns = useMemo(() => {
    if (completedColumns) {
      const completedColumnKeys = completedColumns.map(col => col.key);
      return activeVisibleColumns.filter(key => completedColumnKeys.includes(key));
    }
    return activeVisibleColumns;
  }, [activeVisibleColumns, completedColumns]);

  const orderedActiveColumns = useMemo(() => {
    const columnMap = new Map(columns.map((column) => [column.key, column]));
    return activeVisibleColumns
      .map((columnKey) => columnMap.get(columnKey))
      .filter(Boolean);
  }, [columns, activeVisibleColumns]);

  const orderedCompletedColumns = useMemo(() => {
    const sourceColumns = completedColumns || columns;
    const columnMap = new Map(sourceColumns.map((column) => [column.key, column]));
    return completedVisibleColumns
      .map((columnKey) => columnMap.get(columnKey))
      .filter(Boolean);
  }, [columns, completedColumns, completedVisibleColumns]);

  // Update internal visible columns when default changes
  useEffect(() => {
    if (defaultVisibleColumns.length > 0 && !visibleColumns) {
      setInternalVisibleColumns(defaultVisibleColumns);
    }
  }, [defaultVisibleColumns, visibleColumns]);

  // Filter and sort active data
  const filteredActiveData = useMemo(() => {
    let filtered = activeData;

    // Apply custom filter if provided
    if (customFilter) {
      filtered = customFilter(filtered);
    } else {
      // Default search filtering
      if (searchTerm) {
        filtered = filtered.filter(item => {
          return Object.values(item).some(value => 
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
      }
    }

    // Apply custom sort if provided
    if (customSort) {
      filtered = customSort(filtered, sortConfig);
    } else {
      // Default sorting
      if (sortConfig.key) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[sortConfig.key] || '';
          const bVal = b[sortConfig.key] || '';
          
          if (sortConfig.direction === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
      }
    }

    return filtered;
  }, [activeData, searchTerm, sortConfig, customFilter, customSort]);

  // Filter and sort completed data
  const filteredCompletedData = useMemo(() => {
    let filtered = completedData;

    // Apply same filtering logic as active data
    if (customFilter) {
      filtered = customFilter(filtered);
    } else {
      if (searchTerm) {
        filtered = filtered.filter(item => {
          return Object.values(item).some(value => 
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
      }
    }

    if (customSort) {
      filtered = customSort(filtered, completedSortConfig || sortConfig);
    } else {
      const sortConfigToUse = completedSortConfig || sortConfig;
      if (sortConfigToUse.key) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[sortConfigToUse.key] || '';
          const bVal = b[sortConfigToUse.key] || '';
          
          if (sortConfigToUse.direction === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
      }
    }

    return filtered;
  }, [completedData, searchTerm, sortConfig, completedSortConfig, customFilter, customSort]);

  // Calculate total counts
  const totalActiveCount = activeData.length;
  const filteredActiveCount = filteredActiveData.length;
  const filteredCompletedCount = filteredCompletedData.length;

  // Export handlers
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleCloseExportModal = () => {
    setShowExportModal(false);
  };

  const handleExportModalSave = async ({ data, columns, filename, format }) => {
    try {
      if (format === 'xlsx') {
        await exportToExcel(data, columns, filename);
      } else if (format === 'csv') {
        exportToCSV(data, columns, filename);
      } else {
        throw new Error('PDF export is not supported on this page.');
      }

      setShowExportModal(false);
    } catch (err) {
      console.error('ListViewPage export error:', err);
      window.alert(err?.message || 'Failed to export data');
    }
  };

  // View management handlers
  const handleViewConfigClick = (targetView = null) => {
    // Edit explicit target view when provided, otherwise edit current selected view.
    const currentViewToEdit = targetView || userViews.find(view => {
      const viewId = view.id;
      const currentViewId = parseInt(currentView, 10);
      return viewId === currentViewId;
    });

    setEditingView(currentViewToEdit || null);
    setShowViewConfigModal(true);
  };

  const handleCreateNewView = () => {
    setEditingView(null);
    setShowViewConfigModal(true);
  };

  const handleCloseViewConfigModal = () => {
    setShowViewConfigModal(false);
    setEditingView(null);
  };

  const handleSaveView = async (viewData) => {
    console.log('ListViewPage handleSaveView called with:', viewData);
    if (onSaveView) {
      console.log('Calling parent onSaveView with editingView:', editingView);
      await onSaveView(viewData, editingView);
    } else {
      console.log('No onSaveView function provided');
    }
    setShowViewConfigModal(false);
    setEditingView(null);
  };

  const handleDeleteViewFromModal = (view) => {
    if (!onDeleteView || !view) return;
    setShowViewConfigModal(false);
    setEditingView(null);
    onDeleteView(view);
  };

  // Error state
  if (error) {
    return (
      <div className="list-view-page">
        <div className="container-fluid">
          <div className="error-container">
            <h2>Error Loading {title}</h2>
            <p>{error.message || 'An error occurred while loading data.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="list-view-page">
        <div className="container-fluid">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading {itemLabel}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="list-view-page">
      <div className="container-fluid">
        {/* Page Header */}
        <PageHeader
          title={title}
          totalCount={totalActiveCount}
          filteredCount={filteredActiveCount}
          itemLabel={itemLabel}
          isMobile={isMobile}
          titleAccessory={useViews ? (
            <ViewDropdown
              tableName={tableName}
              currentView={currentView}
              onViewChange={onViewChange}
              onConfigureView={handleViewConfigClick}
              onCreateView={handleCreateNewView}
              onDeleteView={onDeleteView}
              onSetDefaultView={onSetDefaultView}
            />
          ) : null}
        >
          {/* Export Button */}
          {enableExport && exportButtonPosition !== 'afterActions' && (
            <button 
              className="btn btn-outline-secondary"
              onClick={handleExport}
              aria-label={isMobile ? 'Export' : undefined}
              title={isMobile ? 'Export' : undefined}
            >
              <i className="bi bi-upload"></i>
              {!isMobile && 'Export'}
            </button>
          )}

          {/* Action Buttons */}
          {actionButtons.map((button, index) => (
            <button 
              key={index}
              className={button.className || "btn btn-primary"}
              onClick={button.onClick}
              disabled={button.disabled}
              aria-label={isMobile ? button.label : undefined}
              title={isMobile ? button.label : undefined}
            >
              {button.icon && <i className={button.icon}></i>}
              {!isMobile && button.label}
            </button>
          ))}

          {enableExport && exportButtonPosition === 'afterActions' && (
            <button 
              className="btn btn-outline-secondary"
              onClick={handleExport}
              aria-label={isMobile ? 'Export' : undefined}
              title={isMobile ? 'Export' : undefined}
            >
              <i className="bi bi-upload"></i>
              {!isMobile && 'Export'}
            </button>
          )}
        </PageHeader>

        {/* Search and Filters */}
        <SearchFilters
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          extraFilters={extraFilters}
        >
          {searchActions}
        </SearchFilters>



        {/* Active Data Table */}
        <DataTable
          columns={orderedActiveColumns}
          data={filteredActiveData}
          loading={loading}
          sortConfig={sortConfig}
          onSort={onSort}
          onRowClick={onRowClick}
          onRowRightClick={onRowRightClick}
          emptyMessage={`No ${itemLabel} found`}
          filteredMessage={`No ${itemLabel} match your filters`}
          expandedRows={expandedRows}
          expandedRowsData={expandedRowsData || {}}
          renderExpandedRow={renderExpandedRow}
          selectedRows={enableMultiSelect ? selectedRows : undefined}
          onSelectionChange={enableMultiSelect ? onSelectionChange : undefined}
          selectableRowKey={selectableRowKey}
          getRowClassName={getRowClassName}
          onChildToggle={onChildToggle}
          virtualize={virtualizeActiveTable}
          enableColumnResize={enableColumnResize}
          columnWidths={columnWidths}
          onColumnResize={onColumnResize}
        />

        {/* Completed Items Section */}
        {completedData.length > 0 && (
          <div className="completed-section">
            <div 
              className="completed-section-header" 
              onClick={() => setShowCompleted(!showCompleted)}
            >
              <div className="completed-section-title">
                <i className={completedSectionIcon}></i>
                <span>{completedSectionTitle}</span>
                <span className="completed-count-badge">{filteredCompletedCount}</span>
              </div>
              <i className={`bi ${showCompleted ? 'bi-chevron-up' : 'bi-chevron-down'} completed-section-chevron ${showCompleted ? 'expanded' : ''}`}></i>
            </div>
            
            <div className={`completed-section-container ${showCompleted ? 'expanded' : ''}`}>
              <div className="completed-section-table-wrapper">
                <DataTable
                  columns={orderedCompletedColumns}
                  data={filteredCompletedData}
                  loading={loading}
                  sortConfig={completedSortConfig || sortConfig}
                  onSort={onCompletedSort || onSort}
                  onRowClick={onRowClick}
                  onRowRightClick={onRowRightClick}
                  emptyMessage={`No completed ${itemLabel} found`}
                  filteredMessage={`No completed ${itemLabel} match your filters`}
                  expandedRows={expandedRows}
                  expandedRowsData={expandedRowsData || {}}
                  renderExpandedRow={renderExpandedRow}
                  selectedRows={enableMultiSelect ? selectedRows : undefined}
                  onSelectionChange={enableMultiSelect ? onSelectionChange : undefined}
                  selectableRowKey={selectableRowKey}
                  getRowClassName={getRowClassName}
                  onChildToggle={onChildToggle}
                  virtualize={virtualizeCompletedTable}
                  enableColumnResize={enableColumnResize}
                  columnWidths={columnWidths}
                  onColumnResize={onColumnResize}
                />
              </div>
            </div>
          </div>
        )}

        {/* Additional Content */}
        {children}

        {/* Export Modal */}
        {enableExport && (
          <ExportModal 
            show={showExportModal}
            onClose={handleCloseExportModal}
            onExport={handleExportModalSave}
            data={exportData || [...filteredActiveData, ...filteredCompletedData]}
            allColumns={columns}
            visibleColumns={columns.filter(col => activeVisibleColumns.includes(col.key))}
            viewName={currentView !== 'default' ? 'custom' : 'default'}
            entityName={exportFileName || tableName || itemLabel}
            enablePdf={false}
          />
        )}

        {/* View Configuration Modal */}
        {useViews && (
          <ViewConfigModal 
            show={showViewConfigModal}
            onClose={handleCloseViewConfigModal}
            onSave={handleSaveView}
            onDelete={handleDeleteViewFromModal}
            tableName={tableName}
            columns={columns}
            currentConfig={editingView}
            currentColumns={activeVisibleColumns}
            availableFilters={filters}
          />
        )}

        {/* Additional Modals */}
        {additionalModals}

        {/* Toast Notifications */}
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={onCloseToast}
        />
      </div>
    </div>
  );
}